import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LogoAction } from '@/types/TenantSettings';

export type AdminSettingsSection = 'branding' | 'language' | 'working-time' | 'workspace-domain';

export type LanguageDraft = {
  defaultLanguageCode: string;
  enabledLanguageCodes: string[];
};

export type BrandingDraft = {
  name: string;
  logoAction: LogoAction;
};

export type PendingRemovalConfirm =
  | { kind: 'language'; value: string }
  | { kind: 'workspace-domain'; value: string };

export type WorkingTimeDraft = {
  retroactiveEntryWindowDays: number | null;
};

export type WorkspaceDomainDraft = {
  allowList: string[];
};

export type AdminSettingsPageState = {
  activeSection: AdminSettingsSection;
  languageDraft: LanguageDraft;
  brandingDraft: BrandingDraft;
  workingTimeDraft: WorkingTimeDraft;
  workspaceDomainDraft: WorkspaceDomainDraft;
  submitInProgress: boolean;
  lastErrorKey: string | null;
  pendingRemovalConfirm: PendingRemovalConfirm | null;
};

const initialLanguageDraft: LanguageDraft = {
  defaultLanguageCode: '',
  enabledLanguageCodes: [],
};

const initialBrandingDraft: BrandingDraft = {
  name: '',
  logoAction: 'unchanged',
};

const initialState: AdminSettingsPageState = {
  activeSection: 'language',
  languageDraft: initialLanguageDraft,
  brandingDraft: initialBrandingDraft,
  workingTimeDraft: { retroactiveEntryWindowDays: null },
  workspaceDomainDraft: { allowList: [] },
  submitInProgress: false,
  lastErrorKey: null,
  pendingRemovalConfirm: null,
};

export const adminSettingsPageSlice = createSlice({
  name: 'adminSettingsPage',
  initialState,
  reducers: {
    setActiveSection(state, action: PayloadAction<AdminSettingsSection>) {
      state.activeSection = action.payload;
      state.lastErrorKey = null;
    },

    prefillLanguageDraft(state, action: PayloadAction<LanguageDraft>) {
      state.languageDraft = { ...action.payload };
      state.lastErrorKey = null;
    },

    setDefaultLanguageCode(state, action: PayloadAction<string>) {
      const code = action.payload;
      state.languageDraft.defaultLanguageCode = code;
      if (!state.languageDraft.enabledLanguageCodes.includes(code)) {
        state.languageDraft.enabledLanguageCodes = [
          ...state.languageDraft.enabledLanguageCodes,
          code,
        ];
      }
      state.lastErrorKey = null;
    },

    addEnabledLanguageCode(state, action: PayloadAction<string>) {
      const code = action.payload;
      if (!state.languageDraft.enabledLanguageCodes.includes(code)) {
        state.languageDraft.enabledLanguageCodes = [
          ...state.languageDraft.enabledLanguageCodes,
          code,
        ];
      }
      state.lastErrorKey = null;
    },

    requestRemoveEnabledLanguageCode(state, action: PayloadAction<string>) {
      state.pendingRemovalConfirm = { kind: 'language', value: action.payload };
    },

    confirmRemoveEnabledLanguageCode(state) {
      if (!state.pendingRemovalConfirm) return;
      const code = state.pendingRemovalConfirm.value;
      state.languageDraft.enabledLanguageCodes = state.languageDraft.enabledLanguageCodes.filter(
        (c) => c !== code,
      );
      if (state.languageDraft.defaultLanguageCode === code) {
        state.languageDraft.defaultLanguageCode =
          state.languageDraft.enabledLanguageCodes[0] ?? '';
      }
      state.pendingRemovalConfirm = null;
      state.lastErrorKey = null;
    },

    cancelRemoveEnabledLanguageCode(state) {
      state.pendingRemovalConfirm = null;
    },

    prefillBrandingDraft(state, action: PayloadAction<{ name: string }>) {
      state.brandingDraft.name = action.payload.name;
      state.brandingDraft.logoAction = 'unchanged';
      state.lastErrorKey = null;
    },

    setBrandingName(state, action: PayloadAction<string>) {
      state.brandingDraft.name = action.payload;
      state.lastErrorKey = null;
    },

    setBrandingLogoAction(state, action: PayloadAction<LogoAction>) {
      state.brandingDraft.logoAction = action.payload;
      state.lastErrorKey = null;
    },

    prefillWorkingTimeDraft(state, action: PayloadAction<number>) {
      state.workingTimeDraft.retroactiveEntryWindowDays = action.payload;
      state.lastErrorKey = null;
    },

    prefillWorkspaceDomainDraft(state, action: PayloadAction<string[]>) {
      state.workspaceDomainDraft.allowList = [...action.payload];
      state.lastErrorKey = null;
    },

    enableWorkspaceDomain(state, action: PayloadAction<string>) {
      const domain = action.payload;
      if (!state.workspaceDomainDraft.allowList.includes(domain)) {
        state.workspaceDomainDraft.allowList = [...state.workspaceDomainDraft.allowList, domain];
      }
      state.lastErrorKey = null;
    },

    requestRemoveWorkspaceDomain(state, action: PayloadAction<string>) {
      state.pendingRemovalConfirm = { kind: 'workspace-domain', value: action.payload };
    },

    confirmWorkspaceDomainRemoval(state) {
      if (!state.pendingRemovalConfirm || state.pendingRemovalConfirm.kind !== 'workspace-domain') return;
      const domain = state.pendingRemovalConfirm.value;
      state.workspaceDomainDraft.allowList = state.workspaceDomainDraft.allowList.filter(
        (d) => d !== domain,
      );
      state.pendingRemovalConfirm = null;
      state.lastErrorKey = null;
    },

    cancelWorkspaceDomainRemoval(state) {
      state.pendingRemovalConfirm = null;
    },

    setRetroactiveEntryWindowDays(state, action: PayloadAction<number | null>) {
      state.workingTimeDraft.retroactiveEntryWindowDays = action.payload;
      state.lastErrorKey = null;
    },

    setSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },

    setLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },

    resetPage() {
      return initialState;
    },
  },
});

export const {
  setActiveSection,
  prefillLanguageDraft,
  setDefaultLanguageCode,
  addEnabledLanguageCode,
  requestRemoveEnabledLanguageCode,
  confirmRemoveEnabledLanguageCode,
  cancelRemoveEnabledLanguageCode,
  prefillBrandingDraft,
  setBrandingName,
  setBrandingLogoAction,
  prefillWorkingTimeDraft,
  setRetroactiveEntryWindowDays,
  prefillWorkspaceDomainDraft,
  enableWorkspaceDomain,
  requestRemoveWorkspaceDomain,
  confirmWorkspaceDomainRemoval,
  cancelWorkspaceDomainRemoval,
  setSubmitInProgress,
  setLastErrorKey,
  resetPage,
} = adminSettingsPageSlice.actions;

type AppRootState = { adminSettingsPage: AdminSettingsPageState };

export const selectAdminSettingsPage = (state: AppRootState) => state.adminSettingsPage;
